import { api } from "@/lib/api";

export type IntelligencePeriodPreset =
  | "today"
  | "yesterday"
  | "this_week"
  | "previous_week"
  | "this_month"
  | "previous_month"
  | "this_quarter"
  | "previous_quarter"
  | "this_year"
  | "previous_year"
  | "custom";

export type KpiDataAvailability = "AVAILABLE" | "AVAILABLE_WITH_LIMITATIONS" | "NOT_AVAILABLE";

export type CommandCentreKpi = {
  key: string;
  displayName: string;
  name: string;
  domain: string;
  unit: string;
  value: number;
  previousValue: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  dataAvailability: KpiDataAvailability;
  limitations: string | null;
  snapshotAsOf: string | null;
  supportsComparison: boolean;
};

export type DomainSummaryMetric = {
  key: string;
  label: string;
  unit: string;
  value: number;
  previousValue: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  dataAvailability: KpiDataAvailability;
  limitations: string | null;
};

export type DomainSummary = {
  domain: "PHARMACY" | "LAB" | "FINANCE";
  label: string;
  drillDownPath: string;
  metrics: DomainSummaryMetric[];
  limitations: string[];
};

export type CommandCentreResponse = {
  period: {
    preset: string;
    label: string;
    from: string;
    to: string;
    dayCount: number;
  };
  comparisonPeriod: {
    label: string;
    from: string;
    to: string;
    dayCount: number;
  } | null;
  scope: {
    hospitalId: string | null;
    hospitalIds: string[] | null;
    hospitalName: string | null;
    isConsolidated: boolean;
  };
  kpis: CommandCentreKpi[];
  revenue: {
    timeSeries: Array<{
      date: string;
      grossBilling: number;
      netRevenue: number;
      collections: number;
      outstanding: number;
    }>;
    timeSeriesSource: "rollup" | "live" | "mixed";
  };
  funnel: {
    stages: Array<{
      key: string;
      label: string;
      value: number | null;
      available: boolean;
      unavailableReason?: string;
    }>;
    conversions: Array<{
      fromKey: string;
      toKey: string;
      label: string;
      percent: number | null;
      available: boolean;
      unavailableReason?: string;
    }>;
  };
  hospitalComparison: {
    enabled: boolean;
    rows: Array<{
      hospitalId: string;
      hospitalName: string;
      grossBilling: number;
      netRevenue: number;
      collections: number;
      newPatients: number;
      opdVisits: number;
      ipdAdmissions: number;
      surgeries: number;
      leads: number;
    }>;
  };
  managementAttention: ManagementAttentionResponse;
  domainSummaries: DomainSummary[];
  findings?: {
    leakage: {
      openCount: number;
      criticalCount: number;
      topLeakageAmount: number;
      totalPotentialAmount: number;
      topCases: LeakageCase[];
    };
    insights: {
      topInsights: IntelligenceInsight[];
    };
  };
  dataQuality: {
    hasPartialRollups: boolean;
    emptyPeriod: boolean;
    limitedKpis: string[];
    freshness?: {
      aggregationStatus: string;
      detectionStatus: string;
      lastAggregationAt: string | null;
      lastDetectionAt: string | null;
      missingDailyFactsLast30: number;
    };
  };
  benchmarkSummary?: {
    enabled: boolean;
    items: BenchmarkSummaryItem[];
    limitations: string[];
  };
};

export type CommandCentreParams = {
  period?: IntelligencePeriodPreset;
  from?: string;
  to?: string;
  hospitalId?: string;
  compare?: boolean;
};

export type DoctorIntelligenceRow = {
  doctorId: string;
  name: string;
  specialisation: string | null;
  departmentId: string | null;
  departmentName: string | null;
  patientVolume: number;
  opdVisits: number;
  ipdAdmissions: number;
  surgeries: number;
  /** % of OPD patients who had same-doctor IPD after OPD; null if no OPD patients */
  opdToIpdConversionPct: number | null;
  opdPatientsForConversion: number;
  opdPatientsConvertedToIpd: number;
  revenue: number;
  revenuePerPatient: number | null;
  revenueContributionPct: number | null;
  opdRevenue: number;
  ipdRevenue: number;
  labRevenue: number;
  pharmaRevenue: number;
  daycareRevenue: number;
  additionalServicesRevenue: number;
  appointments: number;
  previousRevenue: number | null;
  previousPatientVolume: number | null;
  revenueVariancePct: number | null;
  patientVariancePct: number | null;
  trend: "up" | "down" | "flat" | null;
};

export type DoctorIntelligenceListResponse = {
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  scope: { hospitalId: string | null; hospitalIds: string[] | null };
  totalRevenue: number;
  doctors: DoctorIntelligenceRow[];
  pagination?: { page: number; pageSize: number; total: number };
  limitations: string[];
};

export type DoctorIntelligenceDetailResponse = {
  doctor: {
    doctorId: string;
    name: string;
    specialisation: string | null;
    departmentId: string | null;
    departmentName: string | null;
  };
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  current: DoctorIntelligenceRow;
  statements: string[];
  revenueTrend: Array<{ date: string; revenue: number; patients: number; opdVisits: number }>;
  serviceMix: {
    opd: number;
    additionalServices: number;
    ipd: number;
    lab: number;
    pharma: number;
    daycare: number;
  };
  topPatients: Array<{
    patientId: string;
    patientName: string;
    uhid: string | null;
    totalRevenue: number;
  }>;
  limitations: string[];
};

export type DepartmentIntelligenceRow = {
  departmentId: string;
  departmentName: string;
  hospitalId: string | null;
  doctorCount: number;
  patientVolume: number;
  opdVisits: number;
  ipdAdmissions: number;
  surgeries: number;
  revenue: number;
  revenuePerPatient: number | null;
  revenuePerDoctor: number | null;
  revenueContributionPct: number | null;
  opdRevenue: number;
  ipdRevenue: number;
  labRevenue: number;
  pharmaRevenue: number;
  daycareRevenue: number;
  additionalServicesRevenue: number;
  previousRevenue: number | null;
  previousPatientVolume: number | null;
  revenueVariancePct: number | null;
  patientVariancePct: number | null;
  trend: "up" | "down" | "flat" | null;
};

export type DepartmentIntelligenceListResponse = {
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  scope: { hospitalId: string | null; hospitalIds: string[] | null };
  totalRevenue: number;
  departments: DepartmentIntelligenceRow[];
  limitations: string[];
};

export type DepartmentIntelligenceDetailResponse = {
  department: {
    departmentId: string;
    departmentName: string;
    hospitalId: string | null;
  };
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  current: DepartmentIntelligenceRow;
  statements: string[];
  revenueTrend: Array<{ date: string; revenue: number; opdVisits: number }>;
  serviceMix: {
    opd: number;
    additionalServices: number;
    ipd: number;
    lab: number;
    pharma: number;
    daycare: number;
  };
  doctorContributions: Array<{
    doctorId: string;
    name: string;
    revenue: number;
    revenueContributionPct: number | null;
    patientVolume: number;
  }>;
  limitations: string[];
};

export type OperationsMetricUnit = "COUNT" | "PERCENT" | "DAYS";

export type OperationsMetric = {
  key: string;
  label: string;
  unit: OperationsMetricUnit;
  value: number;
  previousValue: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  snapshotAsOf: string | null;
};

export type UnavailableOperationsMetric = {
  key: string;
  label: string;
  availability: "UNAVAILABLE";
  reason: string;
};

export type WardOccupancyRow = {
  wardId: string;
  wardName: string;
  type: string;
  hospitalId: string;
  totalBeds: number;
  occupied: number;
  available: number;
  occupancyPercent: number;
};

export type DailyOperationsTrendPoint = {
  date: string;
  opdVisits: number;
  ipdAdmissions: number;
  ipdDischarges: number;
};

export type OperationsIntelligenceResponse = {
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  scope: { hospitalId: string | null; hospitalIds: string[] | null };
  metrics: OperationsMetric[];
  unavailable: UnavailableOperationsMetric[];
  bedOccupancy: {
    asOf: string;
    wards: WardOccupancyRow[];
    summary: {
      totalBeds: number;
      totalOccupied: number;
      totalAvailable: number;
      occupancyPercent: number;
    };
  };
  wardOccupancy: WardOccupancyRow[];
  dailyTrend: DailyOperationsTrendPoint[];
  statements: string[];
  limitations: string[];
};

export type PharmacyMetricUnit = "COUNT" | "PERCENT" | "CURRENCY_INR";

export type PharmacyMetric = {
  key: string;
  label: string;
  unit: PharmacyMetricUnit;
  value: number;
  previousValue: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  snapshotAsOf: string | null;
  dataAvailability: KpiDataAvailability;
  limitations: string | null;
};

export type UnavailablePharmacyMetric = {
  key: string;
  label: string;
  availability: "UNAVAILABLE";
  reason: string;
};

export type PharmacyExpiryBucket = {
  key: string;
  label: string;
  minDays: number | null;
  maxDays: number | null;
  quantity: number;
  value: number;
  batchCount: number;
};

export type PharmacySlowMovingRow = {
  batchId: string;
  drugId: string;
  drugName: string;
  batchNo: string;
  expiryDate: string;
  qtyAsOf: number;
  value: number;
  daysSinceLastMovement: number | null;
};

export type PharmacyTopDrugRow = {
  drugId: string;
  medicineName: string;
  totalQty: number;
  mrpValue: number;
  costValue: number;
  margin: number;
};

export type PharmacyDailyTrendPoint = {
  date: string;
  revenue: number;
  issueValue: number;
  itemsSold: number;
};

export type PharmacyHospitalComparisonRow = {
  hospitalId: string;
  hospitalName: string;
  revenue: number;
  issueValue: number;
  stockValue: number;
  nearExpiryValue: number;
  slowMovingValue: number;
};

export type PharmacyIntelligenceResponse = {
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  scope: { hospitalId: string | null; hospitalIds: string[] | null };
  metrics: PharmacyMetric[];
  unavailable: UnavailablePharmacyMetric[];
  revenueSplit: { opd: number; ipd: number };
  expiryBuckets: PharmacyExpiryBucket[];
  slowMoving: {
    lookbackDays: number;
    totalValue: number;
    totalQty: number;
    rows: PharmacySlowMovingRow[];
  };
  topDrugs: PharmacyTopDrugRow[];
  dailyTrend: PharmacyDailyTrendPoint[];
  hospitalComparison: {
    enabled: boolean;
    rows: PharmacyHospitalComparisonRow[];
  };
  statements: string[];
  limitations: string[];
};

export type LabMetricUnit = "COUNT" | "PERCENT" | "CURRENCY_INR" | "HOURS";

export type LabMetric = {
  key: string;
  label: string;
  unit: LabMetricUnit;
  value: number;
  previousValue: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  snapshotAsOf: string | null;
  dataAvailability: KpiDataAvailability;
  limitations: string | null;
};

export type UnavailableLabMetric = {
  key: string;
  label: string;
  availability: "UNAVAILABLE";
  reason: string;
};

export type LabTatStats = {
  averageHours: number | null;
  medianHours: number | null;
  p90Hours: number | null;
  sampleCount: number;
  definition: string;
};

export type LabTopTestRow = {
  testKey: string;
  testName: string;
  volume: number;
  revenue: number;
};

export type LabDailyTrendPoint = {
  date: string;
  revenue: number;
  testVolume: number;
};

export type LabHospitalComparisonRow = {
  hospitalId: string;
  hospitalName: string;
  revenue: number;
  testVolume: number;
  pendingTests: number;
  averageTatHours: number | null;
};

export type LabIntelligenceResponse = {
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  scope: { hospitalId: string | null; hospitalIds: string[] | null };
  metrics: LabMetric[];
  unavailable: UnavailableLabMetric[];
  revenueSplit: { opd: number; ipd: number };
  tat: LabTatStats;
  topTests: LabTopTestRow[];
  dailyTrend: LabDailyTrendPoint[];
  hospitalComparison: {
    enabled: boolean;
    rows: LabHospitalComparisonRow[];
  };
  statements: string[];
  limitations: string[];
};

export type FinanceMetricUnit = "COUNT" | "PERCENT" | "CURRENCY_INR";

export type FinanceMetric = {
  key: string;
  label: string;
  unit: FinanceMetricUnit;
  value: number;
  previousValue: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  snapshotAsOf: string | null;
  dataAvailability: KpiDataAvailability;
  limitations: string | null;
};

export type UnavailableFinanceMetric = {
  key: string;
  label: string;
  availability: "UNAVAILABLE";
  reason: string;
};

export type FinanceCoreKpi = {
  key: string;
  name: string;
  domain: string;
  unit: string;
  value: number;
  previousValue: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  trend: "up" | "down" | "flat" | null;
  dataAvailability: KpiDataAvailability;
  limitations: string | null;
  definition: { supportsComparison: boolean };
  period: { from: string; to: string; label: string };
};

export type FinanceDailyTrendPoint = {
  date: string;
  grossBilling: number;
  netRevenue: number;
  collections: number;
  outstanding: number;
};

export type FinanceReceivablesAgingBucket = {
  key: string;
  label: string;
  minDays: number;
  maxDays: number | null;
  amount: number;
  percent: number;
};

export type FinanceReceivablesAging = {
  snapshotAsOf: string;
  totalOutstanding: number;
  overdueOutstanding: number;
  buckets: FinanceReceivablesAgingBucket[];
  limitations: string[];
};

export type FinanceCollectionEfficiency = {
  percent: number;
  collections: number;
  netRevenue: number;
  formula: string;
  limitations: string[];
};

export type FinanceRevenueCollectionMomRow = {
  monthKey: string;
  label: string;
  netRevenue: number;
  collectedAgainstRevenue: number;
  outstandingAgainstRevenue: number;
  collectionEfficiency: number;
};

export type FinanceProfitability = {
  operatingExpense: number;
  operatingExpensePatient: number;
  operatingExpenseCentral: number;
  operatingProfit: number;
  operatingMarginPercent: number;
  limitations: string[];
};

export type FinanceOneTimeExpenses = {
  capex: number;
  funding: number;
  total: number;
  limitations: string[];
};

export type FinanceCashFlow = {
  cashInflow: number;
  cashOutflow: number;
  netCashFlow: number;
  limitations: string[];
};

export type FinanceHospitalComparisonRow = {
  hospitalId: string;
  hospitalName: string;
  netRevenue: number;
  collections: number;
  outstanding: number;
  collectionEfficiency: number;
};

export type FinanceIntelligenceResponse = {
  period: { from: string; to: string; label: string };
  comparisonPeriod: { from: string; to: string; label: string } | null;
  scope: { hospitalId: string | null; hospitalIds: string[] | null };
  coreKpis: FinanceCoreKpi[];
  metrics: FinanceMetric[];
  unavailable: UnavailableFinanceMetric[];
  collectionEfficiency: FinanceCollectionEfficiency;
  receivablesAging: FinanceReceivablesAging;
  profitability: FinanceProfitability;
  oneTimeExpenses: FinanceOneTimeExpenses;
  cashFlow: FinanceCashFlow;
  dailyTrend: FinanceDailyTrendPoint[];
  timeSeriesSource: "rollup" | "live" | "mixed";
  revenueCollectionMom: FinanceRevenueCollectionMomRow[];
  hospitalComparison: {
    enabled: boolean;
    rows: FinanceHospitalComparisonRow[];
  };
  statements: string[];
  limitations: string[];
  /** Phase H dual-run from double-entry books */
  booksOverlay?: {
    ready: boolean;
    postedVoucherCount: number;
    kpis: Array<{ key: string; label: string; unit: string; value: number }>;
    variance: Array<{
      key: string;
      label: string;
      ops: number;
      books: number;
      difference: number;
      pctDiff: number | null;
    }>;
    note: string;
  } | null;
};

export type IntelligenceSeverity = "CRITICAL" | "ATTENTION" | "OBSERVATION" | "OPPORTUNITY";

export type LeakageCase = {
  findingId: string;
  id: string;
  hospitalId: string;
  hospitalName: string | null;
  ruleKey: string;
  ruleName: string;
  category: string;
  domain: string;
  title: string;
  description: string;
  amount: number | null;
  severity: IntelligenceSeverity;
  status: string;
  recommendedAction: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  detectedAt: string;
  sourceModule: string;
  sourceRecordId: string;
  patientName?: string | null;
  patientPhone?: string | null;
  resolutionNote?: string | null;
};

export type IntelligenceInsight = {
  findingId: string;
  id: string;
  hospitalId: string;
  hospitalName: string | null;
  ruleKey: string | null;
  ruleName: string | null;
  insightType: string | null;
  category: string;
  title: string;
  description: string;
  currentValue: number | null;
  previousValue: number | null;
  changePercent: number | null;
  severity: IntelligenceSeverity;
  status: string;
  recommendedAction: string | null;
  drillDownPath: string | null;
};

export type FindingsListParams = {
  hospitalId?: string;
  period?: IntelligencePeriodPreset;
  from?: string;
  to?: string;
  severity?: string;
  domain?: string;
  type?: string;
  status?: string;
  ruleKey?: string;
  page?: number;
  pageSize?: number;
};

export type LeakageListResponse = {
  summary: {
    openCount: number;
    criticalCount: number;
    attentionCount: number;
    totalPotentialAmount: number;
  };
  pagination: { page: number; pageSize: number; total: number };
  cases: LeakageCase[];
};

export type InsightsListResponse = {
  pagination: { page: number; pageSize: number; total: number };
  insights: IntelligenceInsight[];
};

export type ManagementAttentionItem = {
  id: string;
  ruleKey: string;
  severity: "CRITICAL" | "ATTENTION" | "OBSERVATION" | "OPPORTUNITY";
  category: string;
  title: string;
  description: string;
  metricKey: string;
  currentValue: number | null;
  previousValue: number | null;
  changePercent: number | null;
  threshold: number;
  thresholdLabel: string;
  entityType: "HOSPITAL" | "DOCTOR" | "DEPARTMENT" | "KPI" | "OPERATIONS" | null;
  entityId: string | null;
  entityName: string | null;
  recommendedAction: string | null;
  drillDownPath: string | null;
};

export type ManagementAttentionResponse = {
  placeholder: false;
  message: string;
  items: ManagementAttentionItem[];
  summary: {
    critical: number;
    attention: number;
    observation: number;
    opportunity: number;
    total: number;
  };
  limitations: string[];
};

export type DoctorIntelligenceSortBy = "revenue" | "patients" | "name";

export type IntelligenceListParams = CommandCentreParams & {
  departmentId?: string;
  doctorId?: string;
  page?: number;
  pageSize?: number;
  sortBy?: DoctorIntelligenceSortBy;
};

export type BenchmarkType =
  | "HOSPITAL_VS_GROUP"
  | "HOSPITAL_VS_CHAIN"
  | "HOSPITAL_VS_PEER"
  | "DEPARTMENT_VS_HOSPITAL"
  | "DOCTOR_VS_DEPARTMENT"
  | "DOCTOR_VS_HOSPITAL"
  | "PERIOD_VS_PREVIOUS"
  | "PERIOD_VS_TARGET";

export type BenchmarkStatus = "OK" | "INSUFFICIENT_DATA" | "NOT_APPLICABLE";
export type BenchmarkDirection = "ABOVE" | "BELOW" | "AT" | null;

export type BenchmarkSummaryItem = {
  metricKey: string;
  metricLabel: string;
  currentValue: number | null;
  benchmarkValue: number | null;
  percentageVariance: number | null;
  direction: BenchmarkDirection;
  sampleSize: number;
  benchmarkSource: string;
  status: BenchmarkStatus;
};

export type BenchmarkResult = {
  benchmarkType: BenchmarkType;
  metricKey: string;
  metricLabel: string;
  currentValue: number | null;
  benchmarkValue: number | null;
  absoluteVariance: number | null;
  percentageVariance: number | null;
  percentile: number | null;
  rank: number | null;
  sampleSize: number;
  period: {
    preset: string;
    label: string;
    from: string;
    to: string;
  };
  benchmarkSource: string;
  status: BenchmarkStatus;
  direction: BenchmarkDirection;
  limitations?: string[];
  entityContext?: {
    hospitalId?: string;
    hospitalName?: string;
    departmentId?: string;
    departmentName?: string;
    doctorId?: string;
    doctorName?: string;
  };
};

export type BenchmarksResponse = {
  generatedAt: string;
  scope: {
    hospitalId: string | null;
    hospitalIds: string[] | null;
    hospitalName: string | null;
    isConsolidated: boolean;
  };
  period: BenchmarkResult["period"];
  comparisonPeriod: BenchmarkResult["period"] | null;
  benchmarks: BenchmarkResult[];
  summary: {
    ok: number;
    insufficientData: number;
    notApplicable: number;
    above: number;
    below: number;
    at: number;
  };
  limitations: string[];
  meta?: {
    catalog: Array<{ key: string; label: string; implemented: boolean }>;
    availableBenchmarkTypes: BenchmarkType[];
  };
};

export type BenchmarkListParams = IntelligenceListParams & {
  benchmarkType?: BenchmarkType;
  metrics?: string;
  peerGroupId?: string;
};

export type KpiTargetRow = {
  id: string;
  scopeKey: string;
  kpiKey: string;
  periodKey: string;
  targetValue: number;
  targetType: string;
  active: boolean;
};

export type AttentionRuleSettingRow = {
  ruleKey: string;
  name: string;
  defaultThreshold: number;
  defaultSeverity: string;
  override: {
    threshold: number;
    enabled: boolean;
  } | null;
};

export type InsightRuleSettingRow = {
  ruleKey: string;
  name: string;
  defaults: { enabled: boolean; threshold: number; severity: string };
  override: { enabled: boolean; threshold: number | null } | null;
};

export type LeakageRuleSettingRow = {
  ruleKey: string;
  name: string;
  defaults: { enabled: boolean; minAmount: number; severity: string };
  override: { enabled: boolean; minAmount: number | null } | null;
};

export type BenchmarkGroupRow = {
  id: string;
  name: string;
  description: string | null;
  members: Array<{ hospital: { id: string; name: string } }>;
};

export type ConfigurationAuditEntry = {
  id: string;
  actorId: string;
  actorRole: string;
  hospitalId: string | null;
  groupId: string | null;
  settingType: string;
  settingKey: string;
  oldValue: unknown;
  newValue: unknown;
  reason: string | null;
  createdAt: string;
};

export type ConfigurationAuditResponse = {
  entries: ConfigurationAuditEntry[];
  pagination: { page: number; pageSize: number; total: number };
};

export type DataQualityStatus = "HEALTHY" | "WARNING" | "STALE" | "ERROR";

export type HospitalDataQualityRow = {
  hospitalId: string;
  hospitalName: string;
  aggregation: {
    lastSuccessAt: string | null;
    lastJobType: string;
    status: DataQualityStatus;
    missingDailyFacts: number;
  };
  detection: {
    lastSuccessAt: string | null;
    status: Exclude<DataQualityStatus, "WARNING">;
  };
  kpiAvailability: {
    implemented: number;
    limited: number;
    unavailable: number;
  };
  configurationAnomalies: string[];
};

export type IntelligenceDataQualityResponse = {
  generatedAt: string;
  hospitals: HospitalDataQualityRow[];
  chainSummary?: {
    hospitalCount: number;
    aggregation: Record<DataQualityStatus, number>;
    detection: Record<Exclude<DataQualityStatus, "WARNING">, number>;
    totalMissingDailyFacts: number;
  };
};

export type IntelligenceExportReport =
  | "command-centre"
  | "kpis"
  | "benchmarks"
  | "attention"
  | "insights"
  | "leakage"
  | "doctors"
  | "departments"
  | "operations"
  | "pharmacy"
  | "lab"
  | "finance";

function buildExportQuery(
  params: IntelligenceListParams | FindingsListParams
): Record<string, string | boolean | undefined> {
  const query: Record<string, string | boolean | undefined> = {
    period: params.period ?? "this_month",
  };
  if ("compare" in params && params.compare !== undefined) query.compare = params.compare;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.hospitalId) query.hospitalId = params.hospitalId;
  if ("departmentId" in params && params.departmentId) query.departmentId = params.departmentId;
  if ("doctorId" in params && params.doctorId) query.doctorId = params.doctorId;
  if ("status" in params && params.status) query.status = params.status;
  if ("severity" in params && params.severity) query.severity = params.severity;
  if ("domain" in params && params.domain) query.domain = params.domain;
  if ("ruleKey" in params && params.ruleKey) query.ruleKey = params.ruleKey;
  return query;
}

function buildQuery(params: IntelligenceListParams) {
  const query: Record<string, string | boolean | number | undefined> = {
    period: params.period ?? "this_month",
    compare: params.compare ?? true,
  };
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.hospitalId) query.hospitalId = params.hospitalId;
  if (params.departmentId) query.departmentId = params.departmentId;
  if (params.doctorId) query.doctorId = params.doctorId;
  if (params.page != null) query.page = params.page;
  if (params.pageSize != null) query.pageSize = params.pageSize;
  if (params.sortBy) query.sortBy = params.sortBy;
  return query;
}

function buildFindingsQuery(params: FindingsListParams) {
  const query: Record<string, string | number | undefined> = {};
  if (params.hospitalId) query.hospitalId = params.hospitalId;
  if (params.period) query.period = params.period;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.severity) query.severity = params.severity;
  if (params.domain) query.domain = params.domain;
  if (params.type) query.type = params.type;
  if (params.status) query.status = params.status;
  if (params.ruleKey) query.ruleKey = params.ruleKey;
  if (params.page != null) query.page = params.page;
  if (params.pageSize != null) query.pageSize = params.pageSize;
  return query;
}

function buildBenchmarkQuery(params: BenchmarkListParams) {
  const query = buildQuery(params) as Record<string, string | boolean | undefined>;
  if (params.benchmarkType) query.benchmarkType = params.benchmarkType;
  if (params.metrics) query.metrics = params.metrics;
  if (params.peerGroupId) query.peerGroupId = params.peerGroupId;
  return query;
}

export const intelligenceApi = {
  getCommandCentre(params: CommandCentreParams) {
    return api
      .get<{ data: CommandCentreResponse }>("/api/intelligence/command-centre", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getDoctors(params: IntelligenceListParams) {
    return api
      .get<{ data: DoctorIntelligenceListResponse }>("/api/intelligence/doctors", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getDoctorDetail(doctorId: string, params: CommandCentreParams) {
    return api
      .get<{ data: DoctorIntelligenceDetailResponse }>(
        `/api/intelligence/doctors/${doctorId}`,
        { params: buildQuery(params) }
      )
      .then((r) => r.data.data);
  },

  getDepartments(params: IntelligenceListParams) {
    return api
      .get<{ data: DepartmentIntelligenceListResponse }>("/api/intelligence/departments", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getDepartmentDetail(departmentId: string, params: CommandCentreParams) {
    return api
      .get<{ data: DepartmentIntelligenceDetailResponse }>(
        `/api/intelligence/departments/${departmentId}`,
        { params: buildQuery(params) }
      )
      .then((r) => r.data.data);
  },

  getOperations(params: CommandCentreParams) {
    return api
      .get<{ data: OperationsIntelligenceResponse }>("/api/intelligence/operations", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getPharmacy(params: CommandCentreParams) {
    return api
      .get<{ data: PharmacyIntelligenceResponse }>("/api/intelligence/pharmacy", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getLab(params: CommandCentreParams) {
    return api
      .get<{ data: LabIntelligenceResponse }>("/api/intelligence/lab", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getFinance(params: CommandCentreParams) {
    return api
      .get<{ data: FinanceIntelligenceResponse }>("/api/intelligence/finance", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getAttention(params: CommandCentreParams) {
    return api
      .get<{ data: ManagementAttentionResponse }>("/api/intelligence/attention", {
        params: buildQuery(params),
      })
      .then((r) => r.data.data);
  },

  getFindings(params: FindingsListParams) {
    return api
      .get<{ data: unknown }>("/api/intelligence/findings", { params: buildFindingsQuery(params) })
      .then((r) => r.data.data);
  },

  getLeakage(params: FindingsListParams) {
    return api
      .get<{ data: LeakageListResponse }>("/api/intelligence/leakage", {
        params: buildFindingsQuery(params),
      })
      .then((r) => r.data.data);
  },

  getLeakageDetail(id: string) {
    return api
      .get<{ data: LeakageCase }>(`/api/intelligence/leakage/${id}`)
      .then((r) => r.data.data);
  },

  acknowledgeLeakage(id: string) {
    return api.post(`/api/intelligence/leakage/${id}/acknowledge`).then((r) => r.data);
  },

  resolveLeakage(id: string, note: string) {
    return api.post(`/api/intelligence/leakage/${id}/resolve`, { note }).then((r) => r.data);
  },

  dismissLeakage(id: string, note: string) {
    return api.post(`/api/intelligence/leakage/${id}/dismiss`, { note }).then((r) => r.data);
  },

  getInsights(params: FindingsListParams) {
    return api
      .get<{ data: InsightsListResponse }>("/api/intelligence/insights", {
        params: buildFindingsQuery(params),
      })
      .then((r) => r.data.data);
  },

  getInsightDetail(id: string) {
    return api
      .get<{ data: IntelligenceInsight }>(`/api/intelligence/insights/${id}`)
      .then((r) => r.data.data);
  },

  acknowledgeInsight(id: string) {
    return api.post(`/api/intelligence/insights/${id}/acknowledge`).then((r) => r.data);
  },

  resolveInsight(id: string, note: string) {
    return api.post(`/api/intelligence/insights/${id}/resolve`, { note }).then((r) => r.data);
  },

  dismissInsight(id: string, note: string) {
    return api.post(`/api/intelligence/insights/${id}/dismiss`, { note }).then((r) => r.data);
  },

  getBenchmarks(params: BenchmarkListParams) {
    return api
      .get<{ data: BenchmarksResponse }>("/api/intelligence/benchmarks", {
        params: buildBenchmarkQuery(params),
      })
      .then((r) => r.data.data);
  },

  getBenchmarkMetric(metricKey: string, params: BenchmarkListParams) {
    return api
      .get<{ data: { generatedAt: string; benchmark: BenchmarkResult } }>(
        `/api/intelligence/benchmarks/${metricKey}`,
        { params: buildBenchmarkQuery(params) }
      )
      .then((r) => r.data.data);
  },

  health() {
    return api.get("/api/intelligence/health").then((r: { data: unknown }) => r.data);
  },

  getKpiTargetSettings(params: { hospitalId?: string; groupId?: string }) {
    return api
      .get<{ data: { targets: KpiTargetRow[]; catalog: Array<{ key: string; name: string }> } }>(
        "/api/intelligence/settings/kpi-targets",
        { params }
      )
      .then((r) => r.data.data);
  },

  createKpiTarget(body: {
    hospitalId?: string;
    groupId?: string;
    kpiKey: string;
    periodKey: string;
    targetValue: number;
    targetType?: string;
  }) {
    return api.post("/api/intelligence/settings/kpi-targets", body).then((r) => r.data);
  },

  getAttentionRuleSettings(params: { hospitalId?: string; groupId?: string }) {
    return api
      .get<{ data: { rules: AttentionRuleSettingRow[] } }>(
        "/api/intelligence/settings/attention-rules",
        { params }
      )
      .then((r) => r.data.data);
  },

  getInsightRuleSettings(params: { hospitalId?: string; groupId?: string }) {
    return api
      .get<{ data: { rules: InsightRuleSettingRow[] } }>(
        "/api/intelligence/settings/insight-rules",
        { params }
      )
      .then((r) => r.data.data);
  },

  patchInsightRuleSetting(
    ruleKey: string,
    body: { hospitalId?: string; groupId?: string; enabled?: boolean; threshold?: number }
  ) {
    return api
      .patch(`/api/intelligence/settings/insight-rules/${ruleKey}`, body)
      .then((r) => r.data);
  },

  getLeakageRuleSettings(params: { hospitalId?: string; groupId?: string }) {
    return api
      .get<{ data: { rules: LeakageRuleSettingRow[] } }>(
        "/api/intelligence/settings/leakage-rules",
        { params }
      )
      .then((r) => r.data.data);
  },

  patchLeakageRuleSetting(
    ruleKey: string,
    body: { hospitalId?: string; groupId?: string; enabled?: boolean; minAmount?: number }
  ) {
    return api
      .patch(`/api/intelligence/settings/leakage-rules/${ruleKey}`, body)
      .then((r) => r.data);
  },

  getBenchmarkGroups(params: { hospitalId?: string; groupId?: string }) {
    return api
      .get<{ data: { groups: BenchmarkGroupRow[] } }>(
        "/api/intelligence/settings/benchmark-groups",
        { params }
      )
      .then((r) => r.data.data);
  },

  createBenchmarkGroup(body: { name: string; hospitalIds: string[]; groupId?: string }) {
    return api.post("/api/intelligence/settings/benchmark-groups", body).then((r) => r.data);
  },

  getConfigurationAudit(params: {
    hospitalId?: string;
    settingType?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }) {
    return api
      .get<{ data: ConfigurationAuditResponse }>("/api/intelligence/settings/audit", { params })
      .then((r) => r.data.data);
  },

  getDataQuality(params?: { hospitalId?: string }) {
    return api
      .get<{ data: IntelligenceDataQualityResponse }>("/api/intelligence/data-quality", {
        params: params?.hospitalId ? { hospitalId: params.hospitalId } : {},
      })
      .then((r) => r.data.data);
  },

  exportReport(report: IntelligenceExportReport, params: IntelligenceListParams | FindingsListParams) {
    return api.get<Blob>(`/api/intelligence/export/${report}`, {
      params: buildExportQuery(params),
      responseType: "blob",
    });
  },
};
